/**
 * Tab 3: contacts grouped by contractor, with CA + PM slots per contract.
 *
 * Layout:
 *   - One card per contractor. The header carries the contractor
 *     name and a "+ New contract" affordance.
 *   - Inside the card, each contract has its own row with two
 *     fixed slots: Contract Administrator and Project Manager.
 *     Both slots are principal-side (the operator's team), per
 *     the agreed model.
 *   - Each slot either renders the assigned person's name +
 *     email + phone with edit / remove actions, or an "Assign"
 *     button that opens a picker.
 *   - The picker offers (a) existing principal-side people drawn
 *     from the global contacts list, deduplicated by email; or
 *     (b) a new-person form. Selecting an existing person carries
 *     their name / email / phone over verbatim into the new
 *     assignment.
 *
 * Data shape mapping. The CA / PM slot is one principal-side
 * contact row (``contractor_id IS NULL``, ``contract_id`` set,
 * ``role`` exact-matches ``"Contract administrator"`` or
 * ``"Project manager"``). The page treats the slot as singular:
 * assigning replaces any prior occupant for that (contract, role)
 * pair so reassignments don't accumulate stale rows.
 */

import { type FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import {
  type ApiError,
  type ContactOut,
  type ContractOut,
  type ContractorOut,
  type DocumentRecord,
  createContact,
  createContractor,
  createContract,
  deleteContact,
  deleteContract,
  deleteContractor,
  deleteDocument,
  listContacts,
  listContractors,
  listContracts,
  listDocuments,
  loadConfig,
  updateContract,
  uploadDocument,
} from "../api/client";
import { useToast } from "../Toasts";
import { FileViewerModal } from "./FileViewerModal";

// Documents tagged with these doc_type values are treated as
// candidates for "the contract" in the corpus picker. The set
// matches CONTRACT_DOC_TYPES in VariationPanel so the operator
// can pick from anything they have already uploaded as part of
// the contract corpus, not just rows tagged "base_contract".
const CONTRACT_DOC_TYPES: ReadonlySet<string> = new Set([
  "base_contract",
  "standard",
  "rate_table",
  "explainer",
  "drawing",
]);

const ROLE_CA = "Contract administrator";
const ROLE_PM = "Project manager";

type SlotRole = typeof ROLE_CA | typeof ROLE_PM;

interface Person {
  name: string;
  email: string;
  phone: string;
}

interface SlotContext {
  contractorId: string;
  contractorName: string;
  contractId: string;
  contractRef: string;
  role: SlotRole;
  current: ContactOut | null;
}

interface NewContractContext {
  contractor: ContractorOut;
}

interface RelinkContractContext {
  contractor: ContractorOut;
  contract: ContractOut;
}

export function ContactsPage() {
  const toast = useToast();
  const [contractors, setContractors] = useState<ContractorOut[]>([]);
  const [contracts, setContracts] = useState<ContractOut[]>([]);
  const [contacts, setContacts] = useState<ContactOut[]>([]);
  const [documents, setDocuments] = useState<DocumentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [slotEditor, setSlotEditor] = useState<SlotContext | null>(null);
  const [newContractCtx, setNewContractCtx] =
    useState<NewContractContext | null>(null);
  const [pendingDelete, setPendingDelete] = useState<ContractorOut | null>(null);
  const [pendingContractDelete, setPendingContractDelete] =
    useState<ContractOut | null>(null);
  const [otherDocCtx, setOtherDocCtx] =
    useState<{ contractor: ContractorOut } | null>(null);
  const [viewerDoc, setViewerDoc] = useState<DocumentRecord | null>(null);
  const [relinkCtx, setRelinkCtx] = useState<RelinkContractContext | null>(null);

  const reload = useCallback(async () => {
    const cfg = loadConfig();
    setLoading(true);
    try {
      const [crs, cts, csns, docs] = await Promise.all([
        listContractors(cfg),
        listContracts(cfg),
        listContacts(cfg, { side: "principal" }),
        listDocuments(cfg),
      ]);
      setContractors(crs);
      setContracts(cts);
      setContacts(csns);
      setDocuments(docs);
    } catch (err) {
      toast.show(
        "err",
        (err as ApiError).message ?? "Could not load contacts page",
      );
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Pool of existing principal-side people, deduplicated by email
  // (case-insensitive); rows without an email fall back to name as
  // the dedup key. Used to seed the slot picker so the operator can
  // pick someone they've already added rather than retyping.
  const knownPeople: Person[] = useMemo(() => {
    const seen = new Map<string, Person>();
    for (const c of contacts) {
      const key = (c.email || c.name).trim().toLowerCase();
      if (!seen.has(key) && c.name.trim().length > 0) {
        seen.set(key, {
          name: c.name,
          email: c.email,
          phone: c.phone,
        });
      }
    }
    return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [contacts]);

  function findSlot(
    contractId: string,
    role: SlotRole,
  ): ContactOut | null {
    return (
      contacts.find(
        (c) => c.contract_id === contractId && c.role === role,
      ) ?? null
    );
  }

  function openSlot(
    contractor: ContractorOut,
    contract: ContractOut,
    role: SlotRole,
  ) {
    setSlotEditor({
      contractorId: contractor.id,
      contractorName: contractor.name,
      contractId: contract.id,
      contractRef: contract.reference,
      role,
      current: findSlot(contract.id, role),
    });
  }

  async function clearSlot(slot: ContactOut) {
    if (!confirm(`Remove ${slot.name} from this slot?`)) return;
    const cfg = loadConfig();
    try {
      await deleteContact(cfg, slot.id);
      toast.show("ok", "Slot cleared.");
      await reload();
    } catch (err) {
      toast.show("err", (err as ApiError).message ?? "Could not clear slot");
    }
  }

  async function deleteOtherDocument(doc: DocumentRecord): Promise<void> {
    if (!confirm(`Delete ${doc.original_filename}?`)) return;
    const cfg = loadConfig();
    try {
      await deleteDocument(cfg, doc.document_id);
      setDocuments((prev) =>
        prev.filter((d) => d.document_id !== doc.document_id),
      );
      toast.show("ok", `Deleted ${doc.original_filename}.`);
    } catch (err) {
      toast.show(
        "err",
        (err as ApiError).message ?? "Could not delete file",
      );
    }
  }

  async function assignPerson(ctx: SlotContext, person: Person) {
    const cfg = loadConfig();
    try {
      // Slots are singular per (contract, role). If there is an
      // existing occupant, drop it first so reassignments don't
      // accumulate stale rows; the create that follows is the
      // canonical record.
      if (ctx.current) {
        await deleteContact(cfg, ctx.current.id);
      }
      await createContact(cfg, {
        contractor_id: null,
        contract_id: ctx.contractId,
        name: person.name.trim(),
        role: ctx.role,
        email: person.email.trim(),
        phone: person.phone.trim(),
        notes: "",
        is_primary: false,
      });
      toast.show(
        "ok",
        `${ctx.role} assigned for ${ctx.contractRef}.`,
      );
      setSlotEditor(null);
      await reload();
    } catch (err) {
      toast.show("err", (err as ApiError).message ?? "Could not assign slot");
    }
  }

  async function addContractor(name: string) {
    const cfg = loadConfig();
    try {
      const created = await createContractor(cfg, name);
      setContractors((prev) =>
        [...prev, created].sort((a, b) => a.name.localeCompare(b.name)),
      );
      toast.show("ok", `Contractor "${created.name}" added.`);
    } catch (err) {
      toast.show(
        "err",
        (err as ApiError).message ?? "Could not add contractor",
      );
    }
  }

  // Map a base-contract document to a contract row. The reference
  // is auto-derived from the document's filename so the operator
  // never has to type one. If the resulting reference collides
  // with another contract on the same tenant (the unique-on-
  // (tenant_id, reference) constraint catches this), we suffix a
  // short doc-id chunk and retry once; that keeps the existing
  // schema intact while still producing a friendly default
  // reference for the common case.
  async function addContractFromDocument(
    contractorId: string,
    doc: DocumentRecord,
  ) {
    const cfg = loadConfig();
    const baseRef = doc.original_filename.replace(/\.[^.]+$/, "").slice(0, 100);
    try {
      const created = await createContract(cfg, {
        contractor_id: contractorId,
        reference: baseRef,
        name: doc.original_filename,
        document_id: doc.document_id,
      });
      setContracts((prev) => [...prev, created]);
      setNewContractCtx(null);
      toast.show("ok", `Contract "${created.reference}" added.`);
    } catch (err) {
      const apiErr = err as ApiError;
      if (apiErr.status === 409) {
        // Reference collision. Suffix with the doc id so the
        // operator can still proceed without typing.
        try {
          const suffix = doc.document_id.slice(-6);
          const created = await createContract(cfg, {
            contractor_id: contractorId,
            reference: `${baseRef} (${suffix})`,
            name: doc.original_filename,
            document_id: doc.document_id,
          });
          setContracts((prev) => [...prev, created]);
          setNewContractCtx(null);
          toast.show("ok", `Contract "${created.reference}" added.`);
          return;
        } catch (retryErr) {
          toast.show(
            "err",
            (retryErr as ApiError).message ?? "Could not add contract",
          );
          return;
        }
      }
      toast.show("err", apiErr.message ?? "Could not add contract");
    }
  }

  // Drag-and-drop a file directly onto the picker. Uploads as a
  // base contract document, then immediately maps it to a
  // contract row.
  async function uploadAndAddContract(
    contractorId: string,
    file: File,
  ): Promise<void> {
    const cfg = loadConfig();
    try {
      const uploaded = await uploadDocument(cfg, file, "base_contract");
      setDocuments((prev) => [uploaded, ...prev]);
      await addContractFromDocument(contractorId, uploaded);
    } catch (err) {
      toast.show(
        "err",
        (err as ApiError).message ?? "Could not upload contract",
      );
    }
  }

  // Re-point an existing contract row at a different (or freshly-
  // uploaded) head-contract document. The previous link may have
  // been broken because the underlying document was deleted (the
  // FK is ON DELETE SET NULL, so the contract row survives but
  // points at NULL); the operator's only path back to a working
  // run was previously to delete the contract and re-create it,
  // which loses the contract's reference, contacts and approver
  // email. Relinking preserves all of that.
  async function relinkContractToDocument(
    contract: ContractOut,
    doc: DocumentRecord,
  ): Promise<void> {
    const cfg = loadConfig();
    try {
      const updated = await updateContract(cfg, contract.id, {
        document_id: doc.document_id,
      });
      setContracts((prev) =>
        prev.map((c) => (c.id === contract.id ? updated : c)),
      );
      setRelinkCtx(null);
      toast.show(
        "ok",
        `Relinked ${updated.reference} to ${doc.original_filename}.`,
      );
    } catch (err) {
      toast.show(
        "err",
        (err as ApiError).message ?? "Could not relink contract",
      );
    }
  }

  async function uploadAndRelinkContract(
    contract: ContractOut,
    file: File,
  ): Promise<void> {
    const cfg = loadConfig();
    try {
      const uploaded = await uploadDocument(cfg, file, "base_contract");
      setDocuments((prev) => [uploaded, ...prev]);
      await relinkContractToDocument(contract, uploaded);
    } catch (err) {
      toast.show(
        "err",
        (err as ApiError).message ?? "Could not upload + relink",
      );
    }
  }

  return (
    <main className="contacts-cards-page">
      <header className="contacts-cards-head">
        <div>
          <h2>Contacts</h2>
          <p className="contacts-cards-blurb">
            Everything starts with a contractor. Add one below; every
            contract, Contract administrator, and Project manager hangs
            off that contractor.
          </p>
        </div>
      </header>

      <NewContractorHero
        onSubmit={(name) => void addContractor(name)}
      />

      {loading && contractors.length === 0 ? (
        <div className="empty-row">Loading…</div>
      ) : contractors.length === 0 ? (
        <div className="empty-row">
          No contractors yet. Use the form above to add the first one;
          everything else (contracts, CAs, PMs) hangs off it.
        </div>
      ) : (
        <div className="contractor-cards">
          {contractors.map((contractor) => {
            const its = contracts.filter(
              (ct) => ct.contractor_id === contractor.id,
            );
            return (
              <ContractorCard
                key={contractor.id}
                contractor={contractor}
                contracts={its}
                documents={documents}
                findSlot={findSlot}
                onOpenSlot={openSlot}
                onClearSlot={clearSlot}
                onAddContract={() =>
                  setNewContractCtx({ contractor })
                }
                onAddOtherDocument={() =>
                  setOtherDocCtx({ contractor })
                }
                onDeleteOtherDocument={(doc) => void deleteOtherDocument(doc)}
                onRequestDelete={() => setPendingDelete(contractor)}
                onRequestDeleteContract={(contract) =>
                  setPendingContractDelete(contract)
                }
                onRequestRelinkContract={(contract) =>
                  setRelinkCtx({ contractor, contract })
                }
                onViewDocument={(doc) => setViewerDoc(doc)}
              />
            );
          })}
        </div>
      )}

      {newContractCtx && (
        <ContractCorpusPicker
          contractor={newContractCtx.contractor}
          documents={documents}
          attachedDocIds={
            new Set(
              contracts
                .filter((c) => c.contractor_id === newContractCtx.contractor.id)
                .map((c) => c.document_id)
                .filter((d): d is string => d !== null),
            )
          }
          onCancel={() => setNewContractCtx(null)}
          onPick={(doc) =>
            void addContractFromDocument(newContractCtx.contractor.id, doc)
          }
          onUpload={(file) =>
            uploadAndAddContract(newContractCtx.contractor.id, file)
          }
        />
      )}

      {relinkCtx && (
        <ContractCorpusPicker
          contractor={relinkCtx.contractor}
          documents={documents}
          // Exclude documents already attached to OTHER contracts
          // under the same contractor; the contract being relinked
          // currently has no link, so nothing to exclude on its
          // own behalf.
          attachedDocIds={
            new Set(
              contracts
                .filter(
                  (c) =>
                    c.contractor_id === relinkCtx.contractor.id
                    && c.id !== relinkCtx.contract.id,
                )
                .map((c) => c.document_id)
                .filter((d): d is string => d !== null),
            )
          }
          mode="relink"
          contractReference={relinkCtx.contract.reference}
          onCancel={() => setRelinkCtx(null)}
          onPick={(doc) =>
            void relinkContractToDocument(relinkCtx.contract, doc)
          }
          onUpload={(file) =>
            uploadAndRelinkContract(relinkCtx.contract, file)
          }
        />
      )}

      {slotEditor && (
        <SlotEditor
          ctx={slotEditor}
          knownPeople={knownPeople}
          onCancel={() => setSlotEditor(null)}
          onAssign={(person) => void assignPerson(slotEditor, person)}
        />
      )}

      {pendingDelete && (
        <DeleteContractorDialog
          contractor={pendingDelete}
          contractCount={
            contracts.filter((c) => c.contractor_id === pendingDelete.id).length
          }
          onCancel={() => setPendingDelete(null)}
          onConfirm={async () => {
            const cfg = loadConfig();
            try {
              await deleteContractor(cfg, pendingDelete.id);
              setContractors((prev) =>
                prev.filter((c) => c.id !== pendingDelete.id),
              );
              setPendingDelete(null);
              toast.show("ok", `Deleted ${pendingDelete.name}.`);
            } catch (err) {
              const apiErr = err as ApiError;
              if (apiErr.status === 409) {
                toast.show(
                  "err",
                  "Contractor still owns contracts. Delete the contracts first, then try again.",
                );
              } else {
                toast.show("err", apiErr.message ?? "Could not delete contractor");
              }
            }
          }}
        />
      )}

      {pendingContractDelete && (
        <DeleteContractDialog
          contract={pendingContractDelete}
          slotCount={
            contacts.filter((c) => c.contract_id === pendingContractDelete.id)
              .length
          }
          onCancel={() => setPendingContractDelete(null)}
          onConfirm={async () => {
            const cfg = loadConfig();
            try {
              await deleteContract(cfg, pendingContractDelete.id);
              // The backend cascades to the contract's contact rows;
              // mirror that locally so the page does not show stale
              // CA / PM slots until the next reload.
              setContracts((prev) =>
                prev.filter((c) => c.id !== pendingContractDelete.id),
              );
              setContacts((prev) =>
                prev.filter((c) => c.contract_id !== pendingContractDelete.id),
              );
              setPendingContractDelete(null);
              toast.show(
                "ok",
                `Deleted contract ${pendingContractDelete.reference}.`,
              );
            } catch (err) {
              const apiErr = err as ApiError;
              toast.show(
                "err",
                apiErr.message ?? "Could not delete contract",
              );
            }
          }}
        />
      )}

      {otherDocCtx && (
        <NewOtherDocumentDialog
          contractor={otherDocCtx.contractor}
          contracts={contracts.filter(
            (c) => c.contractor_id === otherDocCtx.contractor.id,
          )}
          onCancel={() => setOtherDocCtx(null)}
          onUploaded={(record) => {
            setDocuments((prev) => [record, ...prev]);
            setOtherDocCtx(null);
            toast.show(
              "ok",
              `Uploaded ${record.original_filename}.`,
            );
          }}
        />
      )}

      {viewerDoc && (
        <FileViewerModal
          doc={viewerDoc}
          onClose={() => setViewerDoc(null)}
        />
      )}
    </main>
  );
}

function ContractorCard({
  contractor,
  contracts,
  documents,
  findSlot,
  onOpenSlot,
  onClearSlot,
  onAddContract,
  onAddOtherDocument,
  onDeleteOtherDocument,
  onRequestDelete,
  onRequestDeleteContract,
  onRequestRelinkContract,
  onViewDocument,
}: {
  contractor: ContractorOut;
  contracts: ContractOut[];
  documents: DocumentRecord[];
  findSlot: (contractId: string, role: SlotRole) => ContactOut | null;
  onOpenSlot: (
    contractor: ContractorOut,
    contract: ContractOut,
    role: SlotRole,
  ) => void;
  onClearSlot: (slot: ContactOut) => void;
  onAddContract: () => void;
  onAddOtherDocument: () => void;
  onDeleteOtherDocument: (doc: DocumentRecord) => void;
  onRequestDelete: () => void;
  onRequestDeleteContract: (contract: ContractOut) => void;
  onRequestRelinkContract: (contract: ContractOut) => void;
  onViewDocument: (doc: DocumentRecord) => void;
}) {
  const otherFilesByContract = useMemo(() => {
    const out = new Map<string, DocumentRecord[]>();
    for (const doc of documents) {
      if (doc.doc_type !== "other" || !doc.contract_id) continue;
      const list = out.get(doc.contract_id) ?? [];
      list.push(doc);
      out.set(doc.contract_id, list);
    }
    return out;
  }, [documents]);

  return (
    <section className="contractor-card">
      <header className="contractor-card-head">
        <h3>{contractor.name}</h3>
        <div className="contractor-card-head-actions">
          <button
            type="button"
            className="btn btn-sm"
            onClick={onAddContract}
          >
            + New contract
          </button>
          <button
            type="button"
            className="btn btn-sm"
            onClick={onAddOtherDocument}
            disabled={contracts.length === 0}
            title={
              contracts.length === 0
                ? "Add a contract first; Other documents file against one"
                : "Add an Other document under one of this contractor's contracts"
            }
          >
            + Other document
          </button>
          <button
            type="button"
            className="btn btn-sm btn-danger"
            onClick={onRequestDelete}
            title={
              contracts.length > 0
                ? "Contractor still owns contracts; delete those first"
                : `Delete ${contractor.name}`
            }
          >
            Delete contractor
          </button>
        </div>
      </header>

      {contracts.length === 0 ? (
        <div className="contractor-card-empty">
          No contracts under this contractor yet. Click "+ New contract".
        </div>
      ) : (
        <ul className="contract-list">
          {contracts.map((contract) => {
            const contractDoc = contract.document_id
              ? documents.find((d) => d.document_id === contract.document_id) ?? null
              : null;
            // Two unlinked states, both invalid for running an
            // assessment: (a) no document_id at all (legacy or the
            // FK was nulled by ON DELETE SET NULL when the head
            // document was deleted), (b) document_id is set but the
            // doc is no longer in the documents list (delete in
            // flight, or backend lost it). The affordance copy
            // distinguishes the two so the operator knows whether
            // they need to relink or just wait for the next poll.
            const isUnlinked = contract.document_id === null;
            const isOrphaned = contract.document_id !== null && contractDoc === null;
            const needsRelink = isUnlinked || isOrphaned;
            return (
            <li
              key={contract.id}
              className={`contract-row${needsRelink ? " contract-row-unlinked" : ""}`}
            >
              <div className="contract-row-head">
                {contractDoc ? (
                  <button
                    type="button"
                    className="contract-ref-link"
                    onClick={() => onViewDocument(contractDoc)}
                    title={`Preview ${contractDoc.original_filename}`}
                  >
                    {contract.reference}
                    {contract.name ? ` — ${contract.name}` : ""}
                  </button>
                ) : (
                  <>
                    <span className="contract-ref">{contract.reference}</span>
                    {contract.name && (
                      <span className="contract-name">{contract.name}</span>
                    )}
                  </>
                )}
                <button
                  type="button"
                  className="btn btn-sm btn-danger contract-row-delete"
                  onClick={() => onRequestDeleteContract(contract)}
                  title={`Delete contract ${contract.reference}`}
                >
                  Delete contract
                </button>
              </div>
              {needsRelink && (
                <div
                  className="contract-row-relink-banner"
                  role="status"
                >
                  <span className="contract-row-relink-label">
                    {isUnlinked
                      ? "Head-contract document deleted or never linked"
                      : "Head-contract document is missing"}
                    {" — variations cannot run until this is relinked."}
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    onClick={() => onRequestRelinkContract(contract)}
                  >
                    Relink head contract
                  </button>
                </div>
              )}
              <div className="slots">
                <Slot
                  label={ROLE_CA}
                  current={findSlot(contract.id, ROLE_CA)}
                  onAssign={() => onOpenSlot(contractor, contract, ROLE_CA)}
                  onClear={onClearSlot}
                />
                <Slot
                  label={ROLE_PM}
                  current={findSlot(contract.id, ROLE_PM)}
                  onAssign={() => onOpenSlot(contractor, contract, ROLE_PM)}
                  onClear={onClearSlot}
                />
              </div>
              <OtherFilesSection
                files={otherFilesByContract.get(contract.id) ?? []}
                onView={onViewDocument}
                onDelete={onDeleteOtherDocument}
              />
            </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function Slot({
  label,
  current,
  onAssign,
  onClear,
}: {
  label: string;
  current: ContactOut | null;
  onAssign: () => void;
  onClear: (slot: ContactOut) => void;
}) {
  return (
    <div className="slot">
      <div className="slot-label">{label}</div>
      {current ? (
        <div className="slot-filled">
          <div className="slot-name">{current.name}</div>
          <div className="slot-meta">
            {current.email && (
              <a href={`mailto:${current.email}`}>{current.email}</a>
            )}
            {current.email && current.phone ? " · " : ""}
            {current.phone && (
              <a href={`tel:${current.phone.replace(/\s+/g, "")}`}>
                {current.phone}
              </a>
            )}
          </div>
          <div className="slot-actions">
            <button
              type="button"
              className="btn btn-sm"
              onClick={onAssign}
            >
              Change
            </button>
            <button
              type="button"
              className="btn btn-sm btn-danger"
              onClick={() => onClear(current)}
            >
              Remove
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className="slot-empty"
          onClick={onAssign}
        >
          + Assign {label.toLowerCase()}
        </button>
      )}
    </div>
  );
}

function SlotEditor({
  ctx,
  knownPeople,
  onCancel,
  onAssign,
}: {
  ctx: SlotContext;
  knownPeople: Person[];
  onCancel: () => void;
  onAssign: (person: Person) => void;
}) {
  // The dialog seeds itself from the current occupant when one
  // exists so the operator can correct a typo without retyping.
  const [name, setName] = useState(ctx.current?.name ?? "");
  const [email, setEmail] = useState(ctx.current?.email ?? "");
  const [phone, setPhone] = useState(ctx.current?.phone ?? "");

  function pickKnown(person: Person) {
    setName(person.name);
    setEmail(person.email);
    setPhone(person.phone);
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    if (name.trim().length === 0) return;
    onAssign({ name, email, phone });
  }

  return (
    <div className="dialog-backdrop" onClick={onCancel} role="presentation">
      <div
        className="dialog"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={`Assign ${ctx.role}`}
      >
        <h3>
          {ctx.current ? "Change" : "Assign"} {ctx.role.toLowerCase()}
        </h3>
        <p className="dialog-blurb">
          {ctx.contractorName} · {ctx.contractRef}
        </p>

        {knownPeople.length > 0 && (
          <div className="known-people">
            <div className="known-people-label">Pick existing person</div>
            <div className="known-people-list">
              {knownPeople.map((p) => (
                <button
                  key={`${p.name}-${p.email}`}
                  type="button"
                  className="known-person"
                  onClick={() => pickKnown(p)}
                >
                  <div className="known-person-name">{p.name}</div>
                  {p.email && (
                    <div className="known-person-email">{p.email}</div>
                  )}
                </button>
              ))}
            </div>
            <div className="known-people-divider">or enter new details</div>
          </div>
        )}

        <form onSubmit={submit} className="dialog-body">
          <div className="field">
            <label htmlFor="slot-name">Name</label>
            <input
              id="slot-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoFocus={!ctx.current}
            />
          </div>
          <div className="field-row">
            <div className="field">
              <label htmlFor="slot-email">Email</label>
              <input
                id="slot-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="slot-phone">Phone</label>
              <input
                id="slot-phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>
          </div>
          <div className="dialog-actions">
            <button type="button" className="btn btn-sm" onClick={onCancel}>
              Cancel
            </button>
            <button type="submit" className="btn btn-sm btn-primary">
              {ctx.current ? "Save change" : "Assign"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function NewContractorHero({
  onSubmit,
}: {
  onSubmit: (name: string) => void;
}) {
  // Inline always-visible action panel. Adding a contractor is the
  // first step of every workflow on this page (contracts hang off
  // contractors, CAs and PMs hang off contracts), so the form is
  // sitting right at the top instead of behind a button. Submit
  // clears the field so the operator can chain rapid adds.
  const [name, setName] = useState("");
  function submit(e: FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (trimmed.length === 0) return;
    onSubmit(trimmed);
    setName("");
  }
  return (
    <form className="hero-add-contractor" onSubmit={submit}>
      <div className="hero-add-step">1</div>
      <div className="hero-add-body">
        <label htmlFor="hero-contractor-name" className="hero-add-label">
          Add a contractor
        </label>
        <p className="hero-add-blurb">
          Start here. Every contract, Contract administrator, and
          Project manager belongs to a contractor.
        </p>
        <div className="hero-add-row">
          <input
            id="hero-contractor-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Contractor name (e.g. Acme Builders)"
            required
          />
          <button
            type="submit"
            className="btn btn-primary"
            disabled={name.trim().length === 0}
          >
            Add contractor
          </button>
        </div>
      </div>
    </form>
  );
}

function ContractCorpusPicker({
  contractor,
  documents,
  attachedDocIds,
  mode = "new",
  contractReference,
  onCancel,
  onPick,
  onUpload,
}: {
  contractor: ContractorOut;
  documents: DocumentRecord[];
  attachedDocIds: Set<string>;
  // "new" creates a new contract row from the picked doc; "relink"
  // re-points an existing contract row at a fresh doc (used when
  // the contract's previous head document was deleted).
  mode?: "new" | "relink";
  contractReference?: string;
  onCancel: () => void;
  onPick: (doc: DocumentRecord) => void;
  onUpload: (file: File) => Promise<void>;
}) {
  // Two paths: pick from the corpus the operator already
  // uploaded, or drop a new file onto the dropzone (which
  // uploads as a base contract document and attaches in one go).
  // Documents already attached to a contract under THIS
  // contractor are dimmed + non-pickable so the operator does
  // not accidentally re-attach the same one.
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);

  const candidates = useMemo(
    () =>
      documents
        // doc_type carries a server default, which the generated type
        // renders as optional; the API always sends it.
        .filter((d) => CONTRACT_DOC_TYPES.has(d.doc_type ?? ""))
        .sort(
          (a, b) =>
            new Date(b.received_at).getTime()
            - new Date(a.received_at).getTime(),
        ),
    [documents],
  );

  async function handleFile(file: File) {
    setUploading(true);
    try {
      await onUpload(file);
    } finally {
      setUploading(false);
    }
  }

  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      void handleFile(file);
    }
  }

  return (
    <div className="dialog-backdrop" onClick={onCancel} role="presentation">
      <div
        className="dialog"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={
          mode === "relink"
            ? `Relink head contract for ${contractReference ?? "contract"}`
            : `New contract under ${contractor.name}`
        }
      >
        <h3>
          {mode === "relink"
            ? `Relink head contract — ${contractReference ?? ""}`
            : "New contract"}
        </h3>
        <p className="dialog-blurb">
          {mode === "relink"
            ? `Pick a head-contract document from the corpus, or drop a new file in. The contract row's reference, contacts and approver email stay as-is.`
            : `Under ${contractor.name}. Pick from the contract corpus or drop a new file in.`}
        </p>

        <div className="corpus-list-wrap">
          <div className="known-people-label">From the corpus</div>
          {candidates.length === 0 ? (
            <div className="ccc-empty">
              The contract corpus is empty. Drop a file below to add
              the first one.
            </div>
          ) : (
            <ul className="corpus-list">
              {candidates.map((doc) => {
                const attached = attachedDocIds.has(doc.document_id);
                return (
                  <li key={doc.document_id}>
                    <button
                      type="button"
                      className={`corpus-row${attached ? " corpus-row-attached" : ""}`}
                      disabled={attached}
                      onClick={() => onPick(doc)}
                      title={
                        attached
                          ? "Already attached to a contract under this contractor"
                          : "Pick this document as the contract"
                      }
                    >
                      <span className="corpus-row-name">
                        {doc.original_filename}
                      </span>
                      <span className="corpus-row-meta">
                        {doc.doc_type || "—"} ·{" "}
                        {(doc.byte_size / 1024).toFixed(0)} KB
                        {attached ? " · already attached" : ""}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div
          className={`corpus-dropzone${dragActive ? " corpus-dropzone-active" : ""}${uploading ? " corpus-dropzone-busy" : ""}`}
          onDragOver={(e) => {
            e.preventDefault();
            setDragActive(true);
          }}
          onDragLeave={() => setDragActive(false)}
          onDrop={onDrop}
        >
          <div className="corpus-dropzone-label">
            {uploading
              ? "Uploading…"
              : dragActive
                ? "Drop to upload + attach"
                : "Drag a contract file here to upload + attach"}
          </div>
          <div className="corpus-dropzone-or">or</div>
          <label className="btn btn-sm btn-primary corpus-dropzone-pick">
            Pick a file from disk
            <input
              type="file"
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  void handleFile(file);
                }
                // Reset the input so the same file can be picked again
                // after a failure.
                e.target.value = "";
              }}
            />
          </label>
        </div>

        <div className="dialog-actions">
          <button type="button" className="btn btn-sm" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function DeleteContractorDialog({
  contractor,
  contractCount,
  onCancel,
  onConfirm,
}: {
  contractor: ContractorOut;
  contractCount: number;
  onCancel: () => void;
  onConfirm: () => Promise<void>;
}) {
  // Typed-DELETE confirmation. Operators have to type the literal
  // word "DELETE" before the destructive button enables — the
  // friction is deliberate; an accidental click on a card-level
  // button shouldn't lose a contractor's data. The contract-count
  // banner above warns about the FK RESTRICT path so operators
  // know to clean up contracts first when the count is non-zero.
  const [confirmation, setConfirmation] = useState("");
  const [busy, setBusy] = useState(false);
  const enabled = confirmation.trim().toUpperCase() === "DELETE";
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!enabled) return;
    setBusy(true);
    try {
      await onConfirm();
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="dialog-backdrop" onClick={onCancel} role="presentation">
      <div
        className="dialog dialog-narrow"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={`Delete contractor ${contractor.name}`}
      >
        <h3>Delete contractor?</h3>
        <p className="dialog-blurb">
          This permanently removes <strong>{contractor.name}</strong>.
          {contractCount > 0 && (
            <>
              {" "}
              The contractor still owns <strong>{contractCount}</strong>{" "}
              contract{contractCount === 1 ? "" : "s"}; deleting will fail
              until those are removed first.
            </>
          )}
        </p>
        <form onSubmit={submit} className="dialog-body">
          <div className="field">
            <label htmlFor="delete-confirm">
              Type <code>DELETE</code> to confirm
            </label>
            <input
              id="delete-confirm"
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
              autoFocus
              autoComplete="off"
              spellCheck={false}
            />
          </div>
          <div className="dialog-actions">
            <button type="button" className="btn btn-sm" onClick={onCancel}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-sm btn-danger"
              disabled={!enabled || busy}
            >
              {busy ? "Deleting…" : "Delete contractor"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DeleteContractDialog({
  contract,
  slotCount,
  onCancel,
  onConfirm,
}: {
  contract: ContractOut;
  slotCount: number;
  onCancel: () => void;
  onConfirm: () => Promise<void>;
}) {
  // Same typed-DELETE confirmation pattern as DeleteContractorDialog.
  // The blurb warns about cascade behaviour: deleting a contract
  // wipes its CA/PM contact slots (the backend cascades), and the
  // operator should know that before they confirm.
  const [confirmation, setConfirmation] = useState("");
  const [busy, setBusy] = useState(false);
  const enabled = confirmation.trim().toUpperCase() === "DELETE";
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!enabled) return;
    setBusy(true);
    try {
      await onConfirm();
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="dialog-backdrop" onClick={onCancel} role="presentation">
      <div
        className="dialog dialog-narrow"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={`Delete contract ${contract.reference}`}
      >
        <h3>Delete contract?</h3>
        <p className="dialog-blurb">
          This permanently removes contract{" "}
          <strong>{contract.reference}</strong>
          {contract.name ? <> ({contract.name})</> : null}.
          {slotCount > 0 && (
            <>
              {" "}
              Its <strong>{slotCount}</strong> assigned contact slot
              {slotCount === 1 ? "" : "s"} (CA / PM) will be removed at the
              same time.
            </>
          )}{" "}
          Any chats or assessments scoped to this contract will lose their
          context binding but will not be deleted.
        </p>
        <form onSubmit={submit} className="dialog-body">
          <div className="field">
            <label htmlFor="delete-contract-confirm">
              Type <code>DELETE</code> to confirm
            </label>
            <input
              id="delete-contract-confirm"
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
              autoFocus
              autoComplete="off"
              spellCheck={false}
            />
          </div>
          <div className="dialog-actions">
            <button type="button" className="btn btn-sm" onClick={onCancel}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-sm btn-danger"
              disabled={!enabled || busy}
            >
              {busy ? "Deleting…" : "Delete contract"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function OtherFilesSection({
  files,
  onView,
  onDelete,
}: {
  files: DocumentRecord[];
  onView: (doc: DocumentRecord) => void;
  onDelete: (doc: DocumentRecord) => void;
}) {
  if (files.length === 0) {
    return null;
  }
  return (
    <div className="other-files">
      <div className="other-files-label">OTHER FILES</div>
      <ul className="other-files-list">
        {files.map((doc) => (
          <li key={doc.document_id} className="other-files-row">
            <button
              type="button"
              className="other-files-name"
              onClick={() => onView(doc)}
              title="Open viewer"
            >
              {doc.original_filename}
            </button>
            <button
              type="button"
              className="other-files-delete"
              onClick={() => onDelete(doc)}
              aria-label={`Delete ${doc.original_filename}`}
              title={`Delete ${doc.original_filename}`}
            >
              ×
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function NewOtherDocumentDialog({
  contractor,
  contracts,
  onCancel,
  onUploaded,
}: {
  contractor: ContractorOut;
  contracts: ContractOut[];
  onCancel: () => void;
  onUploaded: (record: DocumentRecord) => void;
}) {
  const toast = useToast();
  const [contractId, setContractId] = useState<string>(
    contracts[0]?.id ?? "",
  );
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);

  async function ingest(files: File[]): Promise<void> {
    if (files.length === 0) return;
    if (!contractId) {
      toast.show("err", "Pick a contract first.");
      return;
    }
    setBusy(true);
    const cfg = loadConfig();
    try {
      const record = await uploadDocument(cfg, files[0]!, "other", {
        contractId,
      });
      onUploaded(record);
    } catch (err) {
      toast.show(
        "err",
        `Upload failed: ${(err as ApiError).message ?? "unknown"}`,
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="dialog-backdrop" onClick={onCancel} role="presentation">
      <div
        className="dialog dialog-narrow"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Add Other document"
      >
        <h3>Add an Other document</h3>
        <p className="dialog-blurb">
          Filed under {contractor.name}'s contract you choose below.
          The chat will retrieve from this file alongside the contract
          itself when you ask questions about that contract.
        </p>
        <div className="field">
          <label htmlFor="other-doc-contract">Contract</label>
          <select
            id="other-doc-contract"
            value={contractId}
            onChange={(e) => setContractId(e.target.value)}
            disabled={contracts.length === 0}
          >
            {contracts.length === 0 && (
              <option value="">No contracts under this contractor</option>
            )}
            {contracts.map((c) => (
              <option key={c.id} value={c.id}>
                {c.reference}
                {c.name ? ` — ${c.name}` : ""}
              </option>
            ))}
          </select>
        </div>
        <div
          className={`drop drop-compact${drag ? " dragover" : ""}${busy ? " drop-disabled" : ""}`}
          onDragOver={(e) => {
            if (busy) return;
            e.preventDefault();
            setDrag(true);
          }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDrag(false);
            if (busy) return;
            void ingest(Array.from(e.dataTransfer.files ?? []));
          }}
          role="button"
          tabIndex={busy ? -1 : 0}
          onClick={() => {
            if (busy) return;
            document.getElementById("other-doc-input")?.click();
          }}
        >
          <div className="drop-title">
            {busy ? "Uploading…" : "Drop a file here, or click to pick"}
          </div>
          <div className="drop-sub drop-formats">
            PDF, DOCX, XLSX, scans, photos.
          </div>
          <input
            id="other-doc-input"
            type="file"
            style={{ display: "none" }}
            onChange={(e) => {
              void ingest(Array.from(e.target.files ?? []));
              e.target.value = "";
            }}
            disabled={busy}
          />
        </div>
        <div className="dialog-actions">
          <button
            type="button"
            className="btn btn-sm"
            onClick={onCancel}
            disabled={busy}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

